import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { COMPANY, LAST_UPDATED, LAST_UPDATED_EN, isFilled } from '../data/company';
import { FALLBACK_PLANS, formatPrice, normalizePlan } from '../data/plans';
import { getSubscriptionPlanList } from '../services/api';
import './LegalPage.css';

const DistanceSalesPage = () => {
  // Sözleşmedeki fiyat tablosu güncel paketleri göstermeli
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = searchParams.get('lang') === 'en' ? 'en' : 'tr';

  useEffect(() => {
    getSubscriptionPlanList()
      .then((list) => {
        const normalized = (list || []).map(normalizePlan).sort((a, b) => a.sortingId - b.sortingId);
        if (normalized.length) setPlans(normalized);
      })
      .catch(() => {});
  }, []);

  const setLang = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'tr') params.delete('lang');
    else params.set('lang', next);
    setSearchParams(params, { replace: true });
  };

  const refundHref = lang === 'en' ? '/iptal-iade?lang=en' : '/iptal-iade';

  return (
    <div className="legal-page">
      <SEO
        title={lang === 'en' ? 'Distance Sales Agreement' : 'Mesafeli Satış Sözleşmesi'}
        path="/mesafeli-satis-sozlesmesi"
      />
      <div className="legal-page__container">
        <div className="legal-page__toolbar">
          <div className="legal-page__lang" role="group" aria-label="Language">
            <button type="button" aria-pressed={lang === 'tr'} onClick={() => setLang('tr')}>TR</button>
            <button type="button" aria-pressed={lang === 'en'} onClick={() => setLang('en')}>EN</button>
          </div>
        </div>

        {lang === 'en' ? (
          <>
            <h1>Distance Sales Agreement</h1>
            <p className="legal-page__date">Last updated: {LAST_UPDATED_EN}</p>

            <h2>1. Parties</h2>
            <p><strong>SELLER</strong></p>
            <ul>
              <li><strong>Legal name:</strong> {COMPANY.legalName}</li>
              <li><strong>Brand:</strong> {COMPANY.brand}</li>
              <li><strong>Address:</strong> {COMPANY.address}</li>
              <li><strong>Phone:</strong> {COMPANY.phone}</li>
              <li><strong>Email:</strong> {COMPANY.email}</li>
              <li><strong>Tax office / no:</strong> {COMPANY.taxOffice} / {COMPANY.taxNumber}</li>
              {isFilled(COMPANY.mersis) && <li><strong>MERSIS No:</strong> {COMPANY.mersis}</li>}
              {isFilled(COMPANY.kep) && <li><strong>KEP (Registered Electronic Mail) Address:</strong> {COMPANY.kep}</li>}
            </ul>

            <p><strong>BUYER</strong></p>
            <p>
              The platform user identified by the name, surname, email address and contact
              details on their membership account, who accepts this agreement electronically.
            </p>

            <h2>2. Subject of the Agreement</h2>
            <p>
              The subject of this agreement is to determine the rights and obligations of the
              parties, under Turkish Law No. 6502 on the Protection of Consumers and the
              Distance Contracts Regulation, in relation to the sale and performance of the
              digital service subscription — whose features and sale price are specified below —
              ordered electronically by the BUYER through {COMPANY.brand}.
            </p>

            <h2>3. Nature and Price of the Service</h2>
            <p>
              The subject of the agreement is a <strong>digital content and service
              subscription</strong> that does not require physical delivery. Subscriptions are
              sold in monthly periods and automatically renew at the end of each period unless
              cancelled by the BUYER.
            </p>

            <table className="legal-page__table">
              <thead>
                <tr>
                  <th>Package</th>
                  <th>Coverage</th>
                  <th>Price (VAT included)</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.id}>
                    <td>{plan.name}</td>
                    <td>{plan.summary}</td>
                    <td>{plan.isFree ? 'Free' : `${formatPrice(plan.price, plan.currency)} / month`}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p>
              Payment is made via credit or debit card, using 3D Secure verification, through
              the contracted payment institution's secure payment infrastructure. Card details
              are not stored on the SELLER's servers.
            </p>

            <h2>4. Performance of the Service</h2>
            <p>
              Once payment is confirmed, the subscription is activated <strong>immediately</strong> and
              the BUYER gains immediate access to the features offered by the relevant plan. As
              this is a digital service, there is no separate delivery process.
            </p>

            <h2>5. Right of Withdrawal</h2>
            <p>
              Under Article 15 of the Distance Contracts Regulation, <strong>the right of
              withdrawal cannot be exercised</strong> for contracts relating to services performed
              immediately in electronic form and intangible goods delivered instantly to the
              consumer.
            </p>
            <p>
              Nevertheless, in the BUYER's favor, the SELLER undertakes to evaluate refund
              requests made within 14 days of the payment date, provided the service has not
              been used at all. See the{' '}
              <a href={refundHref}>Delivery and Refund Terms</a> page for details.
            </p>

            <h2>6. Cancelling the Subscription</h2>
            <p>
              The BUYER may cancel the subscription at any time from account settings.
              Cancellation takes effect at the end of the current billing period; the BUYER
              continues to benefit from the service until the end of that period. A cancelled
              subscription is not billed for the next period.
            </p>

            <h2>7. General Provisions</h2>
            <ul>
              <li>The BUYER acknowledges having read and understood the pre-contractual information on the service's essential characteristics, sale price, payment method and performance, and confirms this electronically.</li>
              <li>If the SELLER is unable to perform the service on time due to force majeure or extraordinary circumstances preventing the service, the BUYER will be notified.</li>
              <li>If the BUYER misuses the platform, provides false information, or violates the Terms of Use, the SELLER may unilaterally terminate the subscription.</li>
              <li>The principles governing the processing of personal data are set out in the Privacy Policy.</li>
            </ul>

            <h2>8. Dispute Resolution</h2>
            <p>
              Disputes arising from this agreement are subject to the jurisdiction of the
              Consumer Arbitration Committees and Consumer Courts at the BUYER's place of
              residence, within the monetary limits announced by the Turkish Ministry of Trade.
            </p>

            <h2>9. Effective Date</h2>
            <p>
              This agreement takes effect once the BUYER accepts it electronically during the
              payment step. A copy of the agreement is sent to the email address registered to
              the BUYER's account.
            </p>

            <p>
              Questions: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> · {COMPANY.phone}
            </p>
          </>
        ) : (
          <>
            <h1>Mesafeli Satış Sözleşmesi</h1>
            <p className="legal-page__date">Son güncelleme: {LAST_UPDATED}</p>

            <h2>1. Taraflar</h2>
            <p><strong>SATICI</strong></p>
            <ul>
              <li><strong>Ünvan:</strong> {COMPANY.legalName}</li>
              <li><strong>Marka:</strong> {COMPANY.brand}</li>
              <li><strong>Adres:</strong> {COMPANY.address}</li>
              <li><strong>Telefon:</strong> {COMPANY.phone}</li>
              <li><strong>E-posta:</strong> {COMPANY.email}</li>
              <li><strong>Vergi Dairesi / No:</strong> {COMPANY.taxOffice} / {COMPANY.taxNumber}</li>
              {isFilled(COMPANY.mersis) && <li><strong>MERSİS No:</strong> {COMPANY.mersis}</li>}
              {isFilled(COMPANY.kep) && <li><strong>KEP Adresi:</strong> {COMPANY.kep}</li>}
            </ul>

            <p><strong>ALICI</strong></p>
            <p>
              İşbu sözleşmeyi elektronik ortamda onaylayan, üyelik hesabında yer alan ad, soyad,
              e-posta ve iletişim bilgileri ile tanımlı platform kullanıcısıdır.
            </p>

            <h2>2. Sözleşmenin Konusu</h2>
            <p>
              İşbu sözleşmenin konusu, ALICI'nın {COMPANY.brand} üzerinden elektronik ortamda
              sipariş verdiği, aşağıda nitelikleri ve satış bedeli belirtilen dijital hizmet
              aboneliğinin satışı ve ifası ile ilgili olarak 6502 sayılı Tüketicinin Korunması
              Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği hükümleri uyarınca tarafların
              hak ve yükümlülüklerinin belirlenmesidir.
            </p>

            <h2>3. Hizmetin Niteliği ve Bedeli</h2>
            <p>
              Sözleşme konusu hizmet, fiziksel teslimat gerektirmeyen <strong>dijital içerik ve
              hizmet aboneliğidir</strong>. Abonelikler aylık dönemler halinde satılır ve ALICI
              tarafından iptal edilmediği sürece her dönem sonunda otomatik olarak yenilenir.
            </p>

            <table className="legal-page__table">
              <thead>
                <tr>
                  <th>Paket</th>
                  <th>Kapsam</th>
                  <th>Bedel (KDV dahil)</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.id}>
                    <td>{plan.name}</td>
                    <td>{plan.summary}</td>
                    <td>{plan.isFree ? 'Ücretsiz' : `${formatPrice(plan.price, plan.currency)} / ay`}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p>
              Ödeme, anlaşmalı ödeme kuruluşunun güvenli ödeme altyapısı üzerinden 3D Secure
              doğrulaması ile kredi kartı veya banka kartı kullanılarak yapılır. Kart bilgileri SATICI sunucularında saklanmaz.
            </p>

            <h2>4. Hizmetin İfası</h2>
            <p>
              Ödemenin onaylanmasının ardından abonelik <strong>anında</strong> aktif edilir ve
              ALICI ilgili paketin sunduğu özelliklere derhal erişim kazanır. Dijital hizmet niteliği
              gereği ayrıca bir teslimat süreci bulunmamaktadır.
            </p>

            <h2>5. Cayma Hakkı</h2>
            <p>
              Mesafeli Sözleşmeler Yönetmeliği'nin 15. maddesi uyarınca, <strong>elektronik ortamda
              anında ifa edilen hizmetler ile tüketiciye anında teslim edilen gayrimaddi mallara
              ilişkin sözleşmelerde cayma hakkı kullanılamaz.</strong>
            </p>
            <p>
              Bununla birlikte SATICI, ALICI lehine olacak şekilde, hizmetin hiç kullanılmamış
              olması kaydıyla ödeme tarihinden itibaren 14 gün içinde iade talebini
              değerlendirmeyi taahhüt eder. Ayrıntılar için{' '}
              <a href={refundHref}>İptal ve İade Koşulları</a> sayfasına bakınız.
            </p>

            <h2>6. Aboneliğin İptali</h2>
            <p>
              ALICI, aboneliğini dilediği zaman hesap ayarlarından iptal edebilir. İptal, içinde
              bulunulan ödeme döneminin sonunda geçerli olur; dönem sonuna kadar hizmetten
              yararlanmaya devam edilir. İptal edilen abonelik bir sonraki dönemde ücretlendirilmez.
            </p>

            <h2>7. Genel Hükümler</h2>
            <ul>
              <li>ALICI, sözleşme konusu hizmetin temel nitelikleri, satış bedeli ve ödeme şekli ile ifaya ilişkin ön bilgileri okuyup bilgi sahibi olduğunu ve elektronik ortamda gerekli teyidi verdiğini kabul eder.</li>
              <li>SATICI, mücbir sebepler veya hizmeti engelleyen olağanüstü durumlar nedeniyle hizmeti süresinde ifa edemezse durumu ALICI'ya bildirir.</li>
              <li>ALICI'nın platformu kötüye kullanması, sahte bilgi vermesi veya <a href="/kullanim-kosullari">Kullanım Koşulları</a>'nı ihlal etmesi hâlinde SATICI aboneliği tek taraflı sonlandırabilir.</li>
              <li>Kişisel verilerin işlenmesine ilişkin esaslar <a href="/gizlilik">Gizlilik Politikası</a>'nda düzenlenmiştir.</li>
            </ul>

            <h2>8. Uyuşmazlıkların Çözümü</h2>
            <p>
              İşbu sözleşmeden doğabilecek uyuşmazlıklarda, Ticaret Bakanlığı'nca ilan edilen
              parasal sınırlar dâhilinde ALICI'nın yerleşim yerindeki Tüketici Hakem Heyetleri ile
              Tüketici Mahkemeleri yetkilidir.
            </p>

            <h2>9. Yürürlük</h2>
            <p>
              ALICI, ödeme adımında işbu sözleşmeyi elektronik ortamda onayladığında sözleşme
              yürürlüğe girer. Sözleşmenin bir örneği ALICI'nın hesabına kayıtlı e-posta adresine
              gönderilir.
            </p>

            <p>
              Sorularınız için: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> · {COMPANY.phone}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default DistanceSalesPage;
